import {
  chatMessages,
  chatReadMarkers,
  chatRoomParticipants,
  chatRooms,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { attachmentResponseSchema } from './attachment'

// ============================================================================
// Chat Rooms
// ============================================================================

export const selectChatRoomSchema = createSelectSchema(chatRooms)
export const insertChatRoomSchema = createInsertSchema(chatRooms).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// Room with participants relation
export const selectChatRoomWithParticipantsSchema = selectChatRoomSchema.extend(
  {
    participants: z
      .array(
        z.object({
          id: z.number(),
          userId: z.number(),
          role: z.enum(['owner', 'member', 'support']),
          createdAt: z.string(),
          user: z.object({
            id: z.number(),
            firstName: z.string(),
            lastName: z.string(),
            email: z.string(),
          }),
        }),
      )
      .optional(),
  },
)

// For creating a room with participants
export const createChatRoomSchema = z.object({
  name: z.string().min(1),
  type: z.enum(['customer_service', 'group']).default('customer_service'),
  participantIds: z.array(z.number()).optional(),
})

// For updating a room
export const updateChatRoomSchema = insertChatRoomSchema.partial()

// ============================================================================
// Chat Room Participants
// ============================================================================

export const selectChatRoomParticipantSchema =
  createSelectSchema(chatRoomParticipants)
export const insertChatRoomParticipantSchema = createInsertSchema(
  chatRoomParticipants,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// ============================================================================
// Chat Messages
// ============================================================================

export const selectChatMessageSchema = createSelectSchema(chatMessages)
export const insertChatMessageSchema = createInsertSchema(chatMessages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// Message with sender and attachments
export const selectChatMessageWithSenderSchema = selectChatMessageSchema.extend(
  {
    sender: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .optional(),
    attachments: z.array(attachmentResponseSchema).optional(),
  },
)

// For creating a message
export const createChatMessageSchema = z.object({
  roomId: z.number(),
  content: z.string(),
})

// For updating a message (editing)
export const updateChatMessageSchema = z.object({
  content: z.string().min(1),
})

// ============================================================================
// Chat Read Markers
// ============================================================================

export const selectChatReadMarkerSchema = createSelectSchema(chatReadMarkers)
export const insertChatReadMarkerSchema = createInsertSchema(
  chatReadMarkers,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// ============================================================================
// Type Exports
// ============================================================================

export type ChatRoom = z.infer<typeof selectChatRoomSchema>
export type ChatRoomWithParticipants = z.infer<
  typeof selectChatRoomWithParticipantsSchema
>
export type CreateChatRoom = z.infer<typeof createChatRoomSchema>
export type UpdateChatRoom = z.infer<typeof updateChatRoomSchema>

export type ChatRoomParticipant = z.infer<
  typeof selectChatRoomParticipantSchema
>
export type CreateChatRoomParticipant = z.infer<
  typeof insertChatRoomParticipantSchema
>

export type ChatMessage = z.infer<typeof selectChatMessageSchema>
export type ChatMessageWithSender = z.infer<
  typeof selectChatMessageWithSenderSchema
>
export type CreateChatMessage = z.infer<typeof createChatMessageSchema>
export type UpdateChatMessage = z.infer<typeof updateChatMessageSchema>

export type ChatReadMarker = z.infer<typeof selectChatReadMarkerSchema>
export type CreateChatReadMarker = z.infer<typeof insertChatReadMarkerSchema>
